import React from "react";

import Box from "@/ui/atoms/Box";
import DealProcessedFiles from "@/ui/organisms/DealOverview/DealProcessedFiles";
import { DeleteConfirmationModal } from "@/ui/organisms/DealOverview/DeleteConfirmationModal";

import type {
    FileExtractionInfo,
    FileExtractionInfoWithParent,
    FileInfoWithParent,
    ExtractionType,
} from "@/ui/organisms/dealTypes";
import type { UserType } from "@/ui/organisms/userTypes";

import type {
    ViewFormattedDateParams,
    ViewFormattedDateProps,
} from "@/ui/views/ViewFormattedDate";
import { createViewFormattedDate } from "@/ui/views/ViewFormattedDate";

import type { TDealsOverviewTab } from "@/ui/router/routerPaths";

export type ViewProjectDocumentsParams = {
    useState: (params: { projectId: number; databaseId: number }) => {
        deleteModalOpen: boolean;
        fileExtractionInfo: FileExtractionInfo[];
        selectedExtractions: FileExtractionInfoWithParent[];
        selectedDeletableExtractions: FileExtractionInfoWithParent[];
        selectedFiles: FileInfoWithParent[];
        selectedDeletableFiles: FileInfoWithParent[];
        projectFilesIsPending: boolean;
        projectFilesIsError: boolean;
        userType: UserType | undefined;
        userSelectedDatabases: number[];
    };
    useFormattedDateState: ViewFormattedDateParams["useState"];
    useCases: () => {
        setDeleteModalOpenState: (open: boolean) => void;
        deleteFiles: (params: {
            projectId: number;
            databaseId: number;
            fileIds: number[];
        }) => Promise<void>;
        deleteExtractions: (params: {
            projectId: number;
            databaseId: number;
            extractionIds: number[];
        }) => Promise<void>;
        exportExtractions: (params: {
            extractions: {
                extractionId: number;
                extractionType: ExtractionType;
            }[];
            projectId: number;
            databaseId: number;
        }) => void;
        downloadOriginalFiles: (params: {
            fileIds: number[];
            databaseId: number;
            projectId: number;
        }) => Promise<void>;
        toggleExtraction: (extraction: FileExtractionInfoWithParent) => void;
        clearSelection: () => void;
        setSelectedFilesAndExtractions: (
            files: FileInfoWithParent[],
            extractions: FileExtractionInfoWithParent[],
        ) => void;
        getSelectedStatus: (
            fileExtractionInfo:
                | FileExtractionInfoWithParent
                | FileInfoWithParent,
        ) => "selected" | "intermediate" | "unselected";
        setFileAndChildren: (
            files: FileInfoWithParent[],
            setTo: boolean,
            exclusive: boolean,
            fileExtractionInfo: FileExtractionInfo[],
        ) => void;
        handleAddExtractionButtonClicked: (params: {
            projectId: number;
            databaseId: number;
            fileId: number;
            activeTab: TDealsOverviewTab;
        }) => void;
    };
};

export type ViewProjectDocumentsProps = {
    projectId: number;
    databaseId: number;
    getExtractionUrl: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => string;
    getRentRollExtractionUrl: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => string;
    onViewExtraction: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => void;
    onViewRentRollExtraction: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => void;
};

export const createViewProjectDocuments = ({
    useState,
    useCases,
    useFormattedDateState,
}: ViewProjectDocumentsParams) => {
    const ViewFormattedDate = createViewFormattedDate({
        useState: useFormattedDateState,
    });
    const ComponentFormattedDate = (props: {
        date: ViewFormattedDateProps["date"];
    }) => (
        <ViewFormattedDate
            date={props.date}
            parts={["day", "month", "year", "hour", "minute", "second"]}
        />
    );

    const ViewProjectDocuments: React.FC<ViewProjectDocumentsProps> = props => {
        const {
            projectId,
            databaseId,
            getExtractionUrl,
            getRentRollExtractionUrl,
            onViewExtraction,
            onViewRentRollExtraction,
        } = props;

        const {
            deleteModalOpen,
            fileExtractionInfo,
            selectedExtractions,
            selectedDeletableExtractions,
            selectedFiles,
            selectedDeletableFiles,
            projectFilesIsPending,
            projectFilesIsError,
            userType,
        } = useState({ projectId, databaseId });

        const {
            setDeleteModalOpenState,
            deleteFiles,
            deleteExtractions,
            exportExtractions,
            downloadOriginalFiles,
            toggleExtraction,
            clearSelection,
            setSelectedFilesAndExtractions,
            getSelectedStatus,
            setFileAndChildren,
            handleAddExtractionButtonClicked,
        } = useCases();

        const [isDeleting, setIsDeleting] = React.useState(false);

        React.useEffect(() => clearSelection, [clearSelection]);

        const handleDeleteConfirmClicked = async () => {
            if (!projectId) return;
            setIsDeleting(true);
            try {
                await Promise.allSettled([
                    selectedDeletableFiles.length > 0
                        ? deleteFiles({
                              projectId,
                              databaseId,
                              fileIds: selectedDeletableFiles.map(
                                  file => file.fileId,
                              ),
                          })
                        : Promise.resolve(),
                    selectedDeletableExtractions.length > 0
                        ? deleteExtractions({
                              projectId,
                              databaseId,
                              extractionIds: selectedDeletableExtractions.map(
                                  extraction => extraction.extractionId,
                              ),
                          })
                        : Promise.resolve(),
                ]);
            } finally {
                setIsDeleting(false);
                setDeleteModalOpenState(false);
                clearSelection();
            }
        };

        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <DeleteConfirmationModal
                    showModal={deleteModalOpen}
                    fileInfo={selectedDeletableFiles}
                    extractionInfo={selectedDeletableExtractions}
                    onCancel={() => setDeleteModalOpenState(false)}
                    onConfirm={(...args) =>
                        void handleDeleteConfirmClicked(...args)
                    }
                    isConfirmLoading={isDeleting}
                    confirmLoadingText="Deleting..."
                />
                <DealProcessedFiles
                    fileExtractionInfo={fileExtractionInfo}
                    selectedExtractions={selectedExtractions}
                    selectedDeletableExtractions={selectedDeletableExtractions}
                    selectedFiles={selectedFiles}
                    selectedDeletableFiles={selectedDeletableFiles}
                    userType={userType}
                    toggleExtraction={toggleExtraction}
                    clearSelection={clearSelection}
                    getSelectedStatus={getSelectedStatus}
                    setFileAndChildren={setFileAndChildren}
                    setSelectedFilesAndExtractions={
                        setSelectedFilesAndExtractions
                    }
                    onDeleteButtonClicked={() => setDeleteModalOpenState(true)}
                    onAddExtraction={handleAddExtractionButtonClicked}
                    onViewExtraction={onViewExtraction}
                    onViewRentRollExtraction={onViewRentRollExtraction}
                    getExtractionUrl={
                        getExtractionUrl as unknown as React.ComponentProps<
                            typeof DealProcessedFiles
                        >["getExtractionUrl"]
                    }
                    getRentRollExtractionUrl={getRentRollExtractionUrl}
                    onExportExtractions={({ extractions }) =>
                        exportExtractions({
                            extractions,
                            projectId,
                            databaseId,
                        })
                    }
                    onDownloadOriginalFiles={fileIds =>
                        void downloadOriginalFiles({
                            fileIds,
                            databaseId,
                            projectId,
                        })
                    }
                    projectFilesIsPending={projectFilesIsPending}
                    projectFilesIsError={projectFilesIsError}
                    ComponentFormattedDate={ComponentFormattedDate}
                />
            </Box>
        );
    };

    return ViewProjectDocuments;
};
